import React, { useEffect, useMemo, useState } from "react";
import CustomerNav from "@/components/layout/CustomerNav";
import { customerApi, formatApiError, formatIDR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CalendarPlus,
  Mail,
  Ban,
  RotateCw,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Package as PackageIcon,
  User,
  MapPin,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { buildIcs, downloadIcs } from "@/lib/ics";
import {
  AttachmentThumbnails,
  sanitizeVisitHistoryPayload,
  buildHistoryByApptIndex,
  buildDayVisitCards,
  baliTime,
  buildPortalCalendarColumns,
  buildPortalCalendarSlots,
  formatPortalSlotLabel,
  computePortalTileGeometry,
  pagePortalCalendarColumns,
  PORTAL_CAL_START_MIN,
  PORTAL_CAL_SLOT_MIN,
  PORTAL_CAL_MAX_COLS,
} from "@/lib/ClientPortalEngine";
import {
  deriveActiveVisits,
  derivePastVisits,
} from "@/lib/portalVisitClassifier";

/**
 * My Visit tab — compact, date-grouped visit list (Iter-Feb 2026).
 *
 * Requirements:
 *   1. High-Density Card Design — one card per calendar day, ~70%
 *      smaller padding, all text sized `text-xs` (matching the
 *      "No upcoming visits yet…" muted line).
 *   2. One Card per Date — treatments on the same Bali-local day
 *      collapse into a single card. Card header shows the Date
 *      (bold) + Branch name.
 *   3. Internal List Structure — inside each card list every service
 *      with its time and specialist. Package redemptions render
 *      under a Package sub-header, then their children indented.
 *   4. Mandatory Clauses — persistence untouched, live state refresh
 *      via `cocoon:customer-updated` still routed via the existing
 *      engine helpers.
 *
 * Engine boundary: consumes helpers from `ClientPortalEngine.jsx`
 * (IMMUTABLE) but does not modify it.
 */

const LATE_CANCEL_HOURS = 24;

/**
 * Group visits by calendar day. Each day-group contains an ordered
 * list of buckets: package bundles (multiple services under one
 * package_id sharing a package_name) and solo services. Branch is
 * captured on the day card header — if a guest visits two branches
 * the same day (rare), each branch gets its own card.
 *
 * NB (Iter-345): actual grouping logic lives inside
 * `ClientPortalEngine.buildDayVisitCards` so the Guest Portal
 * (My Visit + My Journey) reads from ONE canonical helper. This
 * page-level function is a thin passthrough kept for testid + call
 * ergonomics.
 */
function buildDayCards(appts, historyByAppt, branchesById, packagesById) {
  return buildDayVisitCards(appts, historyByAppt, branchesById, packagesById);
}

const statusPill = (status) => {
  const map = {
    booked: "text-primary",
    in_progress: "text-blue-700",
    completed: "text-emerald-700",
    cancelled: "text-rose-700 line-through",
    no_show: "text-muted-foreground italic",
  };
  return map[status] || "text-muted-foreground";
};

// ---- Upcoming Calendar Grid (Iter-353 + Iter-354) --------------------------
//
// Renders the vertical calendar for future bookings. Mirrors the Staff
// Calendar layout so guests recognise the surface:
//   • sticky top row = date columns
//   • sticky LEFT column = 06:00 → 23:30 time axis, always visible while
//     the guest swipes horizontally through dates
//   • each tile absolute-positioned inside its own date column
//
// Iter-354 responsive layer:
//   • Mobile phones — each date column ≈ 80% of viewport width. CSS
//     scroll-snap keeps the swipe smooth so guests land on ONE date at
//     a time instead of free-scrolling.
//   • Tablets / iPads and laptops — 3 columns visible before the guest
//     scrolls. Column width is fixed at 290 px on `md` and up so 3
//     comfortably fit inside the container's `max-w-5xl` bound.
//   • Sticky time axis — the 60 px vertical time column has
//     `position: sticky; left: 0` and its own bg-card fill so tiles
//     scroll UNDER it, never on top of it.
//   • Font-size — tile title / time / team / branch scale between
//     text-[11px] (mobile) and text-[12px] (sm+) so a 260 px column
//     still reads cleanly.
//
// Horizontal overflow scrolls on every viewport. Pagination is owned
// by the parent — this component only paints what it receives.
const CAL_SLOT_PX = 28;
const CAL_TIME_COL_PX = 60;
const CAL_HEADER_PX = 34;

// Tone the tile per status — muted defaults so upcoming bookings look
// friendly, cancelled visits fade to neutral. Deliberately kept simpler
// than the staff calendar palette because guests don't need the check-in
// / deposit signals.
const tileTone = (status) => {
  switch (status) {
    case "cancelled":
      return "border-rose-300 bg-rose-50 text-rose-900";
    case "no_show":
      return "border-neutral-300 bg-neutral-50 text-neutral-600";
    case "in_progress":
      return "border-blue-300 bg-blue-50 text-blue-900";
    case "completed":
      return "border-emerald-300 bg-emerald-50 text-emerald-900";
    default:
      return "border-primary/40 bg-primary/10 text-foreground hover:bg-primary/15";
  }
};

function UpcomingCalendarGrid({
  slots,
  columns,
  openTileId,
  setOpenTileId,
  onReschedule,
  onCancel,
  onAddToCalendar,
}) {
  const bodyHeight = (slots.length - 1) * CAL_SLOT_PX;

  return (
    <div
      className="rounded-xl border border-border bg-card overflow-x-auto overflow-y-visible portal-cal-scroller"
      data-testid="upcoming-calendar"
    >
      {/* Horizontal flexbox so the sticky-left time axis + swipeable date
          columns cooperate under CSS scroll-snap. */}
      <div className="flex items-stretch min-w-full">
        {/* Sticky vertical time axis — 60 px wide, glued to the left edge
            of the scroll container. Its own header cell floats to the
            top-left corner when the page scrolls vertically. */}
        <div
          className="shrink-0 sticky left-0 z-30 bg-card border-r border-border"
          style={{ width: CAL_TIME_COL_PX }}
          data-testid="cal-time-axis"
        >
          <div
            className="sticky top-0 z-40 bg-card px-2 text-[10px] font-medium tracking-widest text-muted-foreground uppercase border-b border-border flex items-center"
            style={{ height: CAL_HEADER_PX }}
          >
            TIME
          </div>
          <div className="relative" style={{ minHeight: bodyHeight }}>
            {slots.slice(0, -1).map((minute) => {
              const isHour = minute % 60 === 0;
              return (
                <div
                  key={minute}
                  className={
                    "px-2 pt-0.5 text-[10px] leading-none border-t " +
                    (isHour
                      ? "border-border font-medium text-foreground"
                      : "border-border/30 text-muted-foreground")
                  }
                  style={{ height: CAL_SLOT_PX }}
                  data-testid={`cal-time-label-${minute}`}
                >
                  {formatPortalSlotLabel(minute)}
                </div>
              );
            })}
          </div>
        </div>

        {/* Date columns — each column snaps to the container's left edge
            so a mobile swipe lands the next date centred in the viewport. */}
        {columns.length === 0 && (
          <div className="p-2 text-xs text-muted-foreground border-l">
            Nothing booked yet
          </div>
        )}
        {columns.map((c) => (
          <div
            key={c.date_key}
            className="shrink-0 border-r border-border last:border-r-0 portal-cal-col"
            data-testid={`cal-col-${c.date_key}`}
          >
            {/* Sticky column header — vertically pinned so guests can
                scroll DOWN through the time axis and still see which
                date they're looking at. */}
            <div
              className="sticky top-0 z-20 bg-card px-2 text-[11px] sm:text-[12px] font-semibold border-b border-border truncate flex items-center"
              style={{ height: CAL_HEADER_PX }}
              data-testid={`cal-col-header-${c.date_key}`}
              title={c.header}
            >
              {c.header}
            </div>

            {/* Column body — slot ticks + absolute-positioned tiles. */}
            <div className="relative" style={{ minHeight: bodyHeight }}>
              {slots.slice(0, -1).map((minute) => {
                const isHour = minute % 60 === 0;
                return (
                  <div
                    key={minute}
                    className={"border-t " + (isHour ? "border-border" : "border-border/30")}
                    style={{ height: CAL_SLOT_PX }}
                  />
                );
              })}

              {c.tiles.map((t) => {
                const { top, height } = computePortalTileGeometry(
                  t.start_min,
                  t.duration_min,
                  CAL_SLOT_PX,
                );
                const isOpen = openTileId === t.id;
                const startsInFuture =
                  new Date(t.start_iso).getTime() > Date.now();
                const canCancel =
                  t.status !== "cancelled" &&
                  t.status !== "no_show" &&
                  startsInFuture;
                const canReschedule = canCancel;
                return (
                  <Popover
                    key={t.id}
                    open={isOpen}
                    onOpenChange={(v) => setOpenTileId(v ? t.id : "")}
                  >
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={
                          "absolute left-1 right-1 rounded-md border shadow-sm p-1.5 text-left leading-tight overflow-hidden transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 " +
                          tileTone(t.status)
                        }
                        style={{ top, height }}
                        data-testid={`cal-tile-${t.id}`}
                        data-appt-id={t.id}
                        data-status={t.status}
                        title={`${t.title} · ${t.time_range}${t.team_names ? " · " + t.team_names : ""}${t.branch_name ? " · " + t.branch_name : ""}`}
                      >
                        <div className="text-[11px] sm:text-[12px] font-semibold truncate flex items-center gap-1">
                          {t.package_name && (
                            <PackageIcon className="w-3 h-3 shrink-0 text-teal-700" />
                          )}
                          <span data-testid={`cal-tile-title-${t.id}`}>
                            {t.title}
                          </span>
                        </div>
                        <div
                          className="tabular-nums text-[10px] sm:text-[11px] opacity-80"
                          data-testid={`cal-tile-time-${t.id}`}
                        >
                          {t.time_range}
                        </div>
                        {t.team_names && (
                          <div
                            className="text-[10px] sm:text-[11px] opacity-80 truncate inline-flex items-center gap-0.5"
                            data-testid={`cal-tile-team-${t.id}`}
                          >
                            <User className="w-2.5 h-2.5 shrink-0" />
                            {t.team_names}
                          </div>
                        )}
                        {t.branch_name && (
                          <div
                            className="text-[10px] sm:text-[11px] opacity-80 truncate inline-flex items-center gap-0.5"
                            data-testid={`cal-tile-branch-${t.id}`}
                          >
                            <MapPin className="w-2.5 h-2.5 shrink-0" />
                            {t.branch_name}
                          </div>
                        )}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-56 p-2"
                      data-testid={`cal-tile-actions-${t.id}`}
                    >
                      <p className="text-xs font-semibold mb-1 truncate">
                        {t.title}
                      </p>
                      <p className="text-[10px] text-muted-foreground mb-2">
                        {t.time_range}
                        {t.branch_name ? ` · ${t.branch_name}` : ""}
                      </p>
                      <div className="flex flex-col gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="justify-start h-8"
                          onClick={() => onReschedule(t)}
                          disabled={!canReschedule}
                          data-testid={`cal-tile-reschedule-${t.id}`}
                        >
                          <RotateCw className="w-3.5 h-3.5 mr-2" />
                          Reschedule
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="justify-start h-8 text-rose-700 hover:text-rose-700 hover:bg-rose-50"
                          onClick={() => onCancel(t)}
                          disabled={!canCancel}
                          data-testid={`cal-tile-cancel-${t.id}`}
                        >
                          <Ban className="w-3.5 h-3.5 mr-2" />
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="justify-start h-8"
                          onClick={() => onAddToCalendar(t)}
                          data-testid={`cal-tile-add-${t.id}`}
                        >
                          <CalendarPlus className="w-3.5 h-3.5 mr-2" />
                          Add to calendar
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MyAppointments() {
  const { customer } = useAuth();
  const nav = useNavigate();
  const [appts, setAppts] = useState([]);
  const [branches, setBranches] = useState({});
  const [packages, setPackages] = useState({});
  const [reasons, setReasons] = useState([]);
  const [history, setHistory] = useState({ history_by_date: [], count: 0 });
  const [expanded, setExpanded] = useState({}); // appt_id -> bool

  const [cancelTarget, setCancelTarget] = useState(null);
  const [reasonId, setReasonId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // "Other" is a catch-all — the note becomes mandatory so the team can see
  // the real story (mirrors the staff dialog + the server-side guard).
  const otherReasonPicked = ((reasons.find((r) => r.id === reasonId)?.label) || "").trim().toLowerCase() === "other";

  const [invoiceTarget, setInvoiceTarget] = useState(null);
  const [invoiceEmail, setInvoiceEmail] = useState("");
  const [invoiceUpdate, setInvoiceUpdate] = useState(false);
  const [invoiceBusy, setInvoiceBusy] = useState(false);

  // Upcoming calendar — pagination state. Max 6 columns visible; Next/Prev
  // buttons appear only when the guest has more future dates than one page.
  const [calPage, setCalPage] = useState(0);
  // Tile popover state — which appointment id is showing its Reschedule /
  // Cancel actions. Empty string = none open.
  const [openTileId, setOpenTileId] = useState("");

  const load = async () => {
    try {
      const [a, b, p, r, h] = await Promise.allSettled([
        customerApi.get("/appointments/mine"),
        customerApi.get("/branches"),
        customerApi.get("/packages"),
        customerApi.get("/cancellation-reasons?active_only=true"),
        customerApi.get("/customers/me/medical-history"),
      ]);
      if (a.status === "fulfilled") setAppts(a.value.data); else setAppts([]);
      if (b.status === "fulfilled") {
        const bm = {};
        b.value.data.forEach((x) => (bm[x.id] = x));
        setBranches(bm);
      }
      if (p.status === "fulfilled") {
        const pm = {};
        (p.value.data || []).forEach((x) => (pm[x.id] = x));
        setPackages(pm);
      } else {
        setPackages({});
      }
      if (r.status === "fulfilled") setReasons(r.value.data); else setReasons([]);
      if (h.status === "fulfilled") setHistory(sanitizeVisitHistoryPayload(h.value.data));
      else setHistory({ history_by_date: [], count: 0 });
      if (h.status === "rejected") toast.error(formatApiError(h.reason));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
    // Live state refresh — repaint when reception updates the guest OR
    // when the tab regains focus (Iter-355). The tab-focus refresh is what
    // makes a staff-side close / cancel / no-show reflect on the guest
    // portal without a manual reload: the classifier re-runs on the
    // fresh payload and the appointment slides from the top calendar
    // grid into the bottom Past Visits list.
    const onUpdate = () => load();
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("cocoon:customer-updated", onUpdate);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onUpdate);
    return () => {
      window.removeEventListener("cocoon:customer-updated", onUpdate);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const historyByAppt = useMemo(() => buildHistoryByApptIndex(history), [history]);
  // Iter-355 — Section routing rule:
  //   • Upcoming Calendar = active bookings (status ∈ {booked, in_progress})
  //     with a Bali-local start date >= today. `completed`, `cancelled`
  //     and `no_show` NEVER appear in the top calendar and drop straight
  //     into Past Visits on the next refetch — see
  //     `/app/frontend/src/lib/portalVisitClassifier.js`.
  //   • Past Visits List = everything the classifier rejects.
  // The engine (ClientPortalEngine.jsx) stays STRICTLY LOCKED — no
  // helpers were added or modified for this iteration.
  const upcoming = useMemo(() => deriveActiveVisits(appts), [appts]);
  const past = useMemo(() => derivePastVisits(appts), [appts]);

  const upcomingDays = useMemo(
    () => buildDayCards(upcoming, historyByAppt, branches, packages).sort(
      (a, b) => (a.first_iso || "").localeCompare(b.first_iso || ""),
    ),
    [upcoming, historyByAppt, branches, packages],
  );
  const pastDays = useMemo(
    () => buildDayCards(past, historyByAppt, branches, packages).sort(
      (a, b) => (b.first_iso || "").localeCompare(a.first_iso || ""),
    ),
    [past, historyByAppt, branches, packages],
  );

  // ---- Upcoming CALENDAR grid (Iter-353) --------------------------------
  // One column per Bali-local appointment date; vertical axis 06:00–23:30
  // in 30-min slots. Max 6 date columns per page — Next/Prev buttons wire
  // up when the guest has more future dates than fit.
  const upcomingColumns = useMemo(
    () => buildPortalCalendarColumns(upcoming, branches, packages, historyByAppt),
    [upcoming, branches, packages, historyByAppt],
  );
  const calSlots = useMemo(() => buildPortalCalendarSlots(), []);
  const calPageInfo = useMemo(
    () => pagePortalCalendarColumns(upcomingColumns, calPage, PORTAL_CAL_MAX_COLS),
    [upcomingColumns, calPage],
  );
  // Reset to page 0 whenever the set of upcoming dates shrinks below the
  // currently displayed page (e.g. after a cancel).
  useEffect(() => {
    if (calPage > 0 && calPage >= calPageInfo.total_pages) setCalPage(0);
  }, [calPage, calPageInfo.total_pages]);

  const openCancel = (a) => { setCancelTarget(a); setReasonId(""); setNote(""); };

  const submitCancel = async () => {
    if (!cancelTarget || !reasonId) {
      toast.error("Please choose a reason for cancelling.");
      return;
    }
    const reason = reasons.find((r) => r.id === reasonId);
    if ((reason?.label || "").trim().toLowerCase() === "other" && !note.trim()) {
      toast.error('Please add a short note so we know why — it is required for "Other".');
      return;
    }
    setBusy(true);
    try {
      await customerApi.delete(`/appointments/${cancelTarget.id}`, {
        data: { reason_id: reasonId, reason_label: reason?.label, note: note.trim() || undefined },
      });
      toast.success("Appointment cancelled.");
      setCancelTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const reschedule = (a) => {
    const startsInFuture = new Date(a.start_iso).getTime() > Date.now();
    if (a.status === "no_show" || a.status === "cancelled" || !startsInFuture) {
      toast.error("That appointment can't be moved online — please WhatsApp us so the front desk can help.");
      load();
      return;
    }
    nav(`/portal/book?rescheduleFrom=${a.id}&serviceId=${a.service_id}&branchId=${a.branch_id}`);
  };

  const addToCalendar = (a) => {
    const b = branches[a.branch_id];
    const location = b ? `Cocoon Medical Spa · ${b.name}${b.address ? ` (${b.address})` : ""}` : "Cocoon Medical Spa";
    const description = [
      `Service: ${a.service_name}`,
      `Duration: ${a.duration_min} min`,
      b?.phone ? `Contact: ${b.phone}` : null,
    ].filter(Boolean).join("\n");
    const ics = buildIcs({
      uid: a.id, title: `Cocoon · ${a.service_name}`,
      description, location, start: a.start_iso, end: a.end_iso,
    });
    const dt = new Date(a.start_iso);
    const stamp = `${dt.getFullYear()}${String(dt.getMonth() + 1).padStart(2, "0")}${String(dt.getDate()).padStart(2, "0")}`;
    downloadIcs(`cocoon-${stamp}.ics`, ics);
    toast.success("Calendar file downloaded.");
  };

  const openResendInvoice = (h) => {
    setInvoiceTarget(h);
    setInvoiceEmail(customer?.email || "");
    setInvoiceUpdate(false);
  };

  const submitResendInvoice = async () => {
    if (!invoiceTarget) return;
    const email = (invoiceEmail || "").trim();
    if (!email) { toast.error("Please enter an email address."); return; }
    setInvoiceBusy(true);
    try {
      const r = await customerApi.post(
        `/customers/me/receipts/${invoiceTarget.order_id}/email`,
        { email, update_profile: invoiceUpdate },
      );
      toast.success(r.data?.ok ? `Receipt emailed to ${r.data.to}` : `Receipt queued for ${r.data?.to || email}`);
      setInvoiceTarget(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setInvoiceBusy(false); }
  };

  const hoursUntil = (iso) => (new Date(iso).getTime() - Date.now()) / 36e5;
  const isLate = cancelTarget ? hoursUntil(cancelTarget.start_iso) < LATE_CANCEL_HOURS : false;

  // ----- Compact service row ----------------------------------------------
  const teamNameFor = (a, h) => {
    if (h?.team && h.team.length) return h.team.filter((m) => m?.name).map((m) => m.name).join(", ");
    return a.staff_name || "";
  };

  const renderServiceRow = (a, mode, indented = false) => {
    const h = historyByAppt[a.id];
    const team = teamNameFor(a, h);
    const time = baliTime(a.start_iso);
    const isFuture = new Date(a.start_iso).getTime() > Date.now();
    const canCancel = mode === "upcoming" && a.status !== "cancelled" && a.status !== "no_show" && isFuture;
    const canResend = mode === "past" && h?.order_id;
    const hasClinical = mode === "past" && (
      (h?.session_notes || h?.notes || "").trim() ||
      (h?.treatments_done || "").trim() ||
      (h?.follow_up || "").trim() ||
      (Array.isArray(h?.attachments) && h.attachments.length > 0) ||
      h?.order_number
    );
    const isOpen = !!expanded[a.id];

    return (
      <div
        key={a.id}
        className={
          "flex items-start gap-2 py-1 " + (indented ? "pl-4 border-l border-border/60 ml-1.5" : "")
        }
        data-testid={`visit-row-${a.id}`}
      >
        <span className="text-[10px] tabular-nums text-muted-foreground w-10 shrink-0 pt-0.5">
          {time}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5">
            <span className={"text-xs " + statusPill(a.status)} data-testid={`visit-row-name-${a.id}`}>
              {a.service_name}
            </span>
            {team && (
              <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5" data-testid={`visit-row-team-${a.id}`}>
                <User className="w-2.5 h-2.5" />
                {team}
              </span>
            )}
            {a.duration_min && (
              <span className="text-[10px] text-muted-foreground">· {a.duration_min}min</span>
            )}
            {typeof a.price === "number" && a.price > 0 && (
              <span className="text-[10px] text-muted-foreground">· {formatIDR(a.price)}</span>
            )}
          </div>
          {a.status === "cancelled" && a.cancellation_reason_label && (
            <p className="text-[10px] text-muted-foreground italic">Reason: {a.cancellation_reason_label}</p>
          )}
          {hasClinical && isOpen && (
            <div
              className="mt-1 space-y-1 text-[10px] text-muted-foreground"
              data-testid={`visit-detail-${a.id}`}
            >
              {h?.order_number && (
                <p data-testid={`visit-invoice-num-${a.id}`}>Invoice #{h.order_number}</p>
              )}
              {(h?.session_notes || h?.notes) && (
                <p><b>Notes:</b> {h.session_notes || h.notes}</p>
              )}
              {h?.treatments_done && (<p><b>Treatments:</b> {h.treatments_done}</p>)}
              {h?.follow_up && (<p><b>Follow-up:</b> {h.follow_up}</p>)}
              {Array.isArray(h?.attachments) && h.attachments.length > 0 && (
                <AttachmentThumbnails
                  attachments={h.attachments}
                  testidPrefix={`visit-attachments-${a.id}`}
                />
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {mode === "upcoming" && (
            <>
              <button
                type="button"
                onClick={() => addToCalendar(a)}
                title="Add to calendar"
                className="p-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground"
                data-testid={`add-to-calendar-${a.id}`}
              >
                <CalendarPlus className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={() => reschedule(a)}
                title="Reschedule"
                className="p-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground"
                data-testid={`reschedule-appt-${a.id}`}
              >
                <RotateCw className="w-3 h-3" />
              </button>
              {canCancel && (
                <button
                  type="button"
                  onClick={() => openCancel(a)}
                  title="Cancel"
                  className="p-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-rose-700"
                  data-testid={`cancel-appt-${a.id}`}
                >
                  <Ban className="w-3 h-3" />
                </button>
              )}
            </>
          )}
          {canResend && (
            <button
              type="button"
              onClick={() => openResendInvoice(h)}
              title="Resend invoice"
              className="p-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground"
              data-testid={`resend-invoice-btn-${a.id}`}
            >
              <Mail className="w-3 h-3" />
            </button>
          )}
          {hasClinical && (
            <button
              type="button"
              onClick={() => setExpanded((s) => ({ ...s, [a.id]: !s[a.id] }))}
              title={isOpen ? "Hide details" : "Show details"}
              className="p-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground"
              data-testid={`toggle-detail-${a.id}`}
            >
              {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderDayCard = (d, mode) => (
    <Card
      key={d.key}
      className="rounded-xl p-3"
      data-testid={`day-card-${d.date_key}-${d.branch_id || "none"}`}
    >
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <p className="text-sm font-semibold" data-testid={`day-card-date-${d.date_key}`}>
          {d.date_display}
        </p>
        {d.branch_name && (
          <p className="text-xs font-medium text-muted-foreground truncate" data-testid={`day-card-branch-${d.date_key}`}>
            {d.branch_name}
          </p>
        )}
      </div>
      <div className="divide-y divide-border/50">
        {d.buckets.map((bucket) => {
          if (bucket.kind === "package") {
            return (
              <div key={bucket.key} className="py-1.5" data-testid={`bucket-package-${bucket.key}`}>
                <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                  <PackageIcon className="w-3.5 h-3.5 text-teal-700" />
                  <span data-testid={`bucket-package-name-${bucket.key}`}>{bucket.package_name}</span>
                </div>
                <div className="mt-1">
                  {bucket.appts.map((a) => renderServiceRow(a, mode, true))}
                </div>
              </div>
            );
          }
          return (
            <div key={bucket.key} className="py-0.5" data-testid={`bucket-solo-${bucket.key}`}>
              {bucket.appts.map((a) => renderServiceRow(a, mode, false))}
            </div>
          );
        })}
      </div>
    </Card>
  );

  return (
    <div>
      <CustomerNav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <div className="flex items-end justify-between mb-4 gap-4">
          <div>
            <p className="tracking-overline text-[11px] text-muted-foreground">YOUR JOURNEY</p>
            <h1 className="font-editorial text-3xl sm:text-4xl mt-1 sm:mt-2">My visits</h1>
          </div>
          <Button className="rounded-full shrink-0" size="sm" onClick={() => nav("/portal/book")} data-testid="new-booking-btn">
            Book new
          </Button>
        </div>

        <section className="mb-6" data-testid="upcoming-section">
          <div className="flex items-baseline justify-between mb-2 gap-3">
            <h2 className="text-[11px] tracking-overline uppercase text-muted-foreground">
              Upcoming visits
            </h2>
            {calPageInfo.total_pages > 1 && (
              <div className="flex items-center gap-1" data-testid="upcoming-pager">
                <button
                  type="button"
                  onClick={() => setCalPage((p) => Math.max(0, p - 1))}
                  disabled={!calPageInfo.has_prev}
                  className="p-1 rounded-full border border-border/60 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="upcoming-pager-prev"
                  title="Earlier dates"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span
                  className="text-[10px] text-muted-foreground tabular-nums"
                  data-testid="upcoming-pager-label"
                >
                  {calPageInfo.page + 1} / {calPageInfo.total_pages}
                </span>
                <button
                  type="button"
                  onClick={() => setCalPage((p) => Math.min(calPageInfo.total_pages - 1, p + 1))}
                  disabled={!calPageInfo.has_next}
                  className="p-1 rounded-full border border-border/60 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="upcoming-pager-next"
                  title="Later dates"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
          {upcomingColumns.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4" data-testid="upcoming-empty">
              No upcoming visits yet. Book one and it&apos;ll appear here with a countdown.
            </p>
          ) : (
            <UpcomingCalendarGrid
              slots={calSlots}
              columns={calPageInfo.visible}
              openTileId={openTileId}
              setOpenTileId={setOpenTileId}
              onReschedule={(t) => { setOpenTileId(""); reschedule(t.raw); }}
              onCancel={(t) => { setOpenTileId(""); openCancel(t.raw); }}
              onAddToCalendar={(t) => { setOpenTileId(""); addToCalendar(t.raw); }}
            />
          )}
        </section>

        <section data-testid="past-section">
          <h2 className="text-[11px] tracking-overline uppercase text-muted-foreground mb-2">Past visits</h2>
          {pastDays.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4" data-testid="past-empty">
              Your past visits will appear here once your first treatment is complete.
            </p>
          ) : (
            <div className="space-y-2" data-testid="past-list">
              {pastDays.map((d) => renderDayCard(d, "past"))}
            </div>
          )}
        </section>
      </div>

      {/* Cancel dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel your appointment</DialogTitle>
            <DialogDescription>
              {cancelTarget && (
                <>
                  <span className="text-foreground">{cancelTarget.service_name}</span> · {new Date(cancelTarget.start_iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {isLate && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 text-xs">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-amber-800 dark:text-amber-200">
                Late cancellation — this appointment is within {LATE_CANCEL_HOURS} hours. You can still cancel, but please contact us to avoid a possible fee.
              </p>
            </div>
          )}

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="cancel-reason">Reason <span className="text-destructive">*</span></Label>
              <Select value={reasonId} onValueChange={setReasonId}>
                <SelectTrigger id="cancel-reason" className="mt-1" data-testid="cancel-reason-select">
                  <SelectValue placeholder="Select a reason…" />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => (
                    <SelectItem key={r.id} value={r.id} data-testid={`cancel-reason-${r.id}`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="cancel-note">
                {otherReasonPicked ? <>Please tell us why <span className="text-destructive">*</span></> : "Anything else? (optional)"}
              </Label>
              <Textarea id="cancel-note" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Feel free to add a short note for our team."
                className="mt-1" rows={3} data-testid="cancel-note-input" />
              {otherReasonPicked && !note.trim() && (
                <p className="text-[11px] text-destructive mt-1" data-testid="cancel-note-error">
                  A short note is required when the reason is "Other".
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)} data-testid="cancel-abort-btn">Keep it</Button>
            <Button variant="destructive" onClick={submitCancel} disabled={busy || !reasonId || (otherReasonPicked && !note.trim())} data-testid="cancel-confirm-btn">
              {busy ? "Cancelling…" : "Cancel appointment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend Invoice dialog */}
      <Dialog open={!!invoiceTarget} onOpenChange={(v) => !v && setInvoiceTarget(null)}>
        <DialogContent data-testid="resend-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Email your receipt</DialogTitle>
            <DialogDescription>
              {invoiceTarget && (
                <>Send invoice <span className="text-foreground">#{invoiceTarget.order_number}</span> for {invoiceTarget.service_name}.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="invoice-email">Email address</Label>
              <Input
                id="invoice-email"
                type="email"
                value={invoiceEmail}
                onChange={(e) => setInvoiceEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1"
                data-testid="resend-invoice-email-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                We&apos;ll send a copy of the invoice as a printable email. Change the address if you&apos;d prefer to send it somewhere else.
              </p>
            </div>
            {customer?.email && invoiceEmail.trim() && invoiceEmail.trim() !== customer.email && (
              <label className="flex items-start gap-2 text-xs" data-testid="resend-invoice-update-profile-row">
                <Checkbox
                  checked={invoiceUpdate}
                  onCheckedChange={(v) => setInvoiceUpdate(!!v)}
                  data-testid="resend-invoice-update-profile"
                />
                <span>Also update the email on my profile to <b>{invoiceEmail.trim()}</b>.</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceTarget(null)} data-testid="resend-invoice-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={submitResendInvoice}
              disabled={invoiceBusy || !(invoiceEmail || "").trim()}
              data-testid="resend-invoice-confirm-btn"
            >
              {invoiceBusy ? "Sending…" : "Send receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
